import { Injectable } from '@angular/core';
import { combineLatest, mergeMap, Observable, of, take } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import cloneDeep from 'lodash.clonedeep';

import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { DailyStockService, WorkedHoursService } from '..';

@UntilDestroy()
@Injectable()
export class DprPersonnelOfflineService {
  constructor(
    private dailyStockService: DailyStockService,
    private workedHoursService: WorkedHoursService,
    private userDetailsFacade: UserDetailsFacade,
    private offlineFacade: OfflineFacade
  ) {}

  updateDependentPersonnelPrevValues(startDate: Date): Observable<boolean> {
    return combineLatest([
      this.userDetailsFacade.query.projectSetup$,
      this.offlineFacade.query.combinedDprDetailsList$,
    ]).pipe(
      untilDestroyed(this),
      take(1),
      mergeMap(([projectSetup, dprDetailsList]) => {
        const personnelList = projectSetup?.personnelList ?? [];
        const dprDetailsCopy = cloneDeep(dprDetailsList);
        personnelList.forEach((personnel) => {
          const items = dprDetailsCopy
            .filter((x) => new Date(x.dprDateTime) >= new Date(startDate))
            .sort((a, b) => {
              return (
                new Date(a.dprDateTime).valueOf() -
                new Date(b.dprDateTime).valueOf()
              );
            });

          for (let i = 1; i < items.length; i++) {
            const prevItem = (
              items[i - 1].dprPersonnel.dprPersonnelDetails || []
            ).find((x) => x.personnelId === personnel.id);
            const currentItem = (
              items[i].dprPersonnel.dprPersonnelDetails || []
            ).find((x) => x.personnelId === personnel.id);

            if (prevItem && currentItem) {
              const prev = prevItem.previous || 0;
              const today = prevItem.today || 0;
              currentItem.previous = Number((prev + today).toFixed(2));
              currentItem.dailyStockDetails =
                this.dailyStockService.onPreviousDayChanged(
                  prevItem.dailyStockDetails,
                  currentItem,
                  currentItem.dailyStockDetails
                );
              currentItem.workedHoursDetails =
                this.workedHoursService.onPreviousDayChanged(
                  prevItem,
                  currentItem,
                  projectSetup
                );

              this.offlineFacade.setDprDetails(cloneDeep(items[i]));
            }
          }
        });
        return of(true);
      })
    );
  }
}
